import { catalogProductsApi } from "@/modules/catalog/presentation/api/client";
import type { IngredientListRow } from "@/modules/inventory/domain/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";
import { LayoutGridIcon, PlusIcon, Trash2Icon, XIcon } from "lucide-react";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { inventoryIngredientsApi } from "../../api/client";
import { useIngredientQuery } from "../../api/queries";
import { formatLakDisplay, formatQtyDisplay } from "@/shared/lib/format-number";
import { PoItemCatalogPickerDialog } from "./PoItemCatalogPickerDialog";
import { poItemTypeLabel } from "./purchaseLaoLabels";
import {
  mergeCatalogPicksIntoDraftLines,
  newDraftLine,
  type PoDraftLineRow,
} from "./poDraftUtils";
import { poCatalogItemKey, type PoCatalogPick } from "./poCatalogTypes";

type RetailVariantOption = {
  id: string;
  name: string;
  productName: string;
};

type PoItemOption = IngredientListRow | RetailVariantOption;

function isRetailVariant(item: PoItemOption): item is RetailVariantOption {
  return "productName" in item;
}

type PurchaseOrderLineEditorProps = {
  lines: PoDraftLineRow[];
  onChange: (lines: PoDraftLineRow[]) => void;
  readOnly?: boolean;
};

function usePoLineItemLabel(line: PoDraftLineRow): string {
  const ingredient = useIngredientQuery(
    line.itemType === "INGREDIENT" && line.itemId ? line.itemId : "",
  );
  const retail = useQuery({
    queryKey: ["catalog", "retail-variant", line.itemId],
    queryFn: () => catalogProductsApi.getRetailVariant(line.itemId),
    enabled: line.itemType === "RETAIL_VARIANT" && !!line.itemId,
    staleTime: Number.POSITIVE_INFINITY,
  });

  if (!line.itemId) return "";
  if (line.itemType === "INGREDIENT" && ingredient.data) {
    return `${ingredient.data.name} (${ingredient.data.receiveUnit || ingredient.data.baseUnit})`;
  }
  if (line.itemType === "RETAIL_VARIANT" && retail.data) {
    return `${retail.data.productName} – ${retail.data.name}`;
  }
  return "ກໍາລັງໂຫຼດ...";
}

function LineItemPicker({
  line,
  onUpdate,
}: {
  line: PoDraftLineRow;
  onUpdate: (patch: Partial<PoDraftLineRow>) => void;
}) {
  const [catalogOpen, setCatalogOpen] = useState(false);
  const label = usePoLineItemLabel(line);

  const itemLookupQueryFn = useMemo(
    () =>
      ({ search, pageParam }: { search: string; pageParam: number }) =>
        line.itemType === "INGREDIENT"
          ? inventoryIngredientsApi.lookup({
              search: search || undefined,
              page: pageParam,
              limit: 20,
            })
          : catalogProductsApi.listRetailVariantsLookup({
              search: search || undefined,
              page: pageParam,
              limit: 20,
            }),
    [line.itemType],
  );

  const itemPreloadQueryFn = useMemo(
    () => (id: string) =>
      line.itemType === "INGREDIENT"
        ? inventoryIngredientsApi.get(id).catch(() => null)
        : catalogProductsApi.getRetailVariant(id),
    [line.itemType],
  );

  const onCatalogPick = (picks: PoCatalogPick[]) => {
    const pick = picks[0];
    if (!pick) return;
    onUpdate({
      itemType: pick.itemType,
      itemId: pick.itemId,
    });
  };

  return (
    <div className="min-w-[240px] space-y-2">
      <Tabs
        value={line.itemType}
        onValueChange={(v) =>
          onUpdate({
            itemType: v as "INGREDIENT" | "RETAIL_VARIANT",
            itemId: "",
          })
        }
      >
        <TabsList className="grid h-8 w-full grid-cols-2">
          <TabsTrigger value="INGREDIENT" className="text-xs">
            ວັດຖຸດິບ
          </TabsTrigger>
          <TabsTrigger value="RETAIL_VARIANT" className="text-xs">
            ຕົວແບບ
          </TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="flex gap-1">
        <Button
          type="button"
          variant="outline"
          className="h-9 min-w-0 flex-1 justify-start font-normal"
          onClick={() => setCatalogOpen(true)}
        >
          <LayoutGridIcon className="mr-2 size-4 shrink-0 opacity-70" />
          <span className="truncate">
            {line.itemId ? label : "ເລືອກຈາກ catalog..."}
          </span>
        </Button>
        {line.itemId ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-9 shrink-0"
            aria-label="ລ້າງລາຍການ"
            onClick={() => onUpdate({ itemId: "" })}
          >
            <XIcon className="size-4" />
          </Button>
        ) : null}
      </div>

      <PoItemCatalogPickerDialog
        open={catalogOpen}
        onOpenChange={setCatalogOpen}
        mode="single"
        initialItemType={line.itemType}
        onConfirm={onCatalogPick}
      />

      <InfiniteCombobox<PoItemOption>
        queryKey={["po-draft", "item", line.itemType, line.key]}
        queryFn={itemLookupQueryFn}
        preloadQueryFn={itemPreloadQueryFn}
        getLabel={(item) =>
          isRetailVariant(item)
            ? `${item.productName} – ${item.name}`
            : `${item.name} (${item.receiveUnit || item.baseUnit})`
        }
        getValue={(item) => item.id}
        placeholder="ຄົ້ນຫາໄວ..."
        value={line.itemId}
        onValueChange={(id) => onUpdate({ itemId: id })}
        clearable
        className="h-8 text-xs"
      />
    </div>
  );
}

export function PurchaseOrderLineEditor({
  lines,
  onChange,
  readOnly,
}: PurchaseOrderLineEditorProps) {
  const [bulkCatalogOpen, setBulkCatalogOpen] = useState(false);

  const existingKeys = useMemo(() => {
    const set = new Set<string>();
    for (const l of lines) {
      if (l.itemId) set.add(poCatalogItemKey(l.itemType, l.itemId));
    }
    return set;
  }, [lines]);

  const updateLine = (key: string, patch: Partial<PoDraftLineRow>) => {
    onChange(
      lines.map((l) => (l.key === key ? { ...l, ...patch } : l)),
    );
  };

  const removeLine = (key: string) => {
    onChange(lines.filter((l) => l.key !== key));
  };

  const addLine = () => onChange([...lines, newDraftLine()]);

  const subtotal = lines.reduce(
    (sum, l) => sum + l.qtyOrdered * l.unitCostLak,
    0,
  );

  if (readOnly) {
    return (
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>#</TableHead>
            <TableHead>ລາຍການ</TableHead>
            <TableHead>ປະເພດ</TableHead>
            <TableHead className="text-right">ຈຳນວນ</TableHead>
            <TableHead className="text-right">ລາຄາ/ຫນ່ວຍ</TableHead>
            <TableHead className="text-right">ລວມ</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {lines.map((line, i) => (
            <TableRow key={line.key}>
              <TableCell>{i + 1}</TableCell>
              <TableCell>{line.itemId || "—"}</TableCell>
              <TableCell>{poItemTypeLabel(line.itemType)}</TableCell>
              <TableCell className="text-right">
                {formatQtyDisplay(line.qtyOrdered)}
              </TableCell>
              <TableCell className="text-right">
                {formatLakDisplay(line.unitCostLak)}
              </TableCell>
              <TableCell className="text-right">
                {formatLakDisplay(line.qtyOrdered * line.unitCostLak)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  }

  return (
    <div className="space-y-3">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[280px]">ລາຍການ</TableHead>
            <TableHead className="w-[100px]">ຈຳນວນ</TableHead>
            <TableHead className="w-[120px]">ລາຄາ (₭/ຫນ່ວຍ)</TableHead>
            <TableHead>ໝາຍເຫດ</TableHead>
            <TableHead className="w-[48px]" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {lines.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} className="text-center text-muted-foreground">
                ຍັງບໍ່ມີບັນທັດ — ກົດເພີ່ມແຖວ ຫຼື ເລືອກຈາກ catalog
              </TableCell>
            </TableRow>
          ) : (
            lines.map((line) => (
              <TableRow key={line.key}>
                <TableCell>
                  <LineItemPicker
                    line={line}
                    onUpdate={(patch) => updateLine(line.key, patch)}
                  />
                </TableCell>
                <TableCell>
                  <Input
                    type="number"
                    min={0.001}
                    step="any"
                    value={line.qtyOrdered}
                    onChange={(e) =>
                      updateLine(line.key, {
                        qtyOrdered: Number(e.target.value) || 0,
                      })
                    }
                  />
                </TableCell>
                <TableCell>
                  <Input
                    type="number"
                    min={0}
                    step={1}
                    value={line.unitCostLak}
                    onChange={(e) =>
                      updateLine(line.key, {
                        unitCostLak: Math.max(
                          0,
                          Math.round(Number(e.target.value) || 0),
                        ),
                      })
                    }
                  />
                </TableCell>
                <TableCell>
                  <Input
                    value={line.note ?? ""}
                    onChange={(e) =>
                      updateLine(line.key, {
                        note: e.target.value.trim() ? e.target.value : null,
                      })
                    }
                    placeholder="ທາງເລືອກ"
                  />
                </TableCell>
                <TableCell>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removeLine(line.key)}
                    aria-label="ລຶບແຖວ"
                  >
                    <Trash2Icon className="size-4 text-destructive" />
                  </Button>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" size="sm" onClick={addLine}>
            <PlusIcon className="size-4" />
            ເພີ່ມແຖວ
          </Button>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => setBulkCatalogOpen(true)}
          >
            <LayoutGridIcon className="size-4" />
            ເພີ່ມຈາກ catalog
          </Button>
        </div>
        <p className="font-medium text-sm">
          ລວມປະມານ: {formatLakDisplay(Math.round(subtotal))}
        </p>
      </div>

      <PoItemCatalogPickerDialog
        open={bulkCatalogOpen}
        onOpenChange={setBulkCatalogOpen}
        mode="multiple"
        onConfirm={(picks) =>
          onChange(mergeCatalogPicksIntoDraftLines(lines, picks))
        }
        excludeKeys={existingKeys}
      />
    </div>
  );
}
